import { useCallback, useContext, useState } from 'react';
import { useParams } from 'react-router';

import { ArrowBack, ArrowForward, ChevronRight } from '@mui/icons-material';
import { AlertColor, Badge, Box, Skeleton, Typography } from '@mui/material';

import queryString from 'query-string';

import Page from '@/components/Page';
import UpdatesDialog from '@/components/Updates';
import { violationsConfig } from '@/components/Updates/config/config';
import Alert from '@/components/alert';
import Button from '@/components/button';
import { ViolationDetailsSteps } from '@/constants/driverSteps';
import {
  ViolationContext,
  ViolationProvider
} from '@/contexts/ViolationContext';
import { useAppSelector } from '@/hooks/redux';
import { getPathWithLocalParams } from '@/hooks/urlParams';
import useDriver from '@/hooks/useDriver';
import useWorkspaceNavigate from '@/hooks/useWorkspaceNavigate';
import { URLParams } from '@/models/misc';
import { Updates } from '@/models/updates';
import getRoute from '@/utils/getRoute';
import NotFound from '@/views/misc/notFound';

import ViolationLayout from './ViolationLayout';

const ViolationContainer = () => {
  const navigate = useWorkspaceNavigate();
  const { postId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { violationData, resetState, isLoading, isError } =
    useContext(ViolationContext);
  const user = useAppSelector((state) => state.user.currentUser);

  const urlParams = queryString.parse(window.location.search);
  const previousPostId = violationData?.previousPostId;
  const nextPostId = violationData?.nextPostId;
  const updatesCount = violationData?.updates?.length || 0;
  const currentPost = violationData?.currentPosition;
  const totalPosts = violationData?.totalCount;

  const handleClickPreviousPost = () => {
    resetState();
    if (previousPostId) {
      navigate(
        getRoute.violations.DETAIL(previousPostId, urlParams as URLParams)
      );
    }
  };

  const handleClickNextPost = () => {
    resetState();
    if (nextPostId) {
      navigate(getRoute.violations.DETAIL(nextPostId, urlParams as URLParams));
    }
  };

  const handleGoBack = () => {
    navigate(getPathWithLocalParams(getRoute.violations.LIST()));
  };

  const violation: Updates.Dialog | undefined = violationData
    ? {
        postId: Number(postId),
        evaluationId: violationData.violations[0]?.evaluationId,
        creatorName: violationData.creator?.name,
        updates: violationData.updates || []
      }
    : undefined;

  useDriver({
    isLoading,
    steps: ViolationDetailsSteps
  });

  const getTermsAlert = useCallback((): {
    severity: AlertColor | undefined;
    message: string;
    action: string;
  } => {
    if (!violationData?.uploadDate || violationData?.isExpired)
      return { severity: undefined, action: '', message: '' };

    const termDate = new Date(user.workspaceStorageTerms.violation);
    const createTime = new Date(violationData.uploadDate);

    const diffInMs = createTime.getTime() - termDate.getTime();
    const daysUntilDeletion = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (daysUntilDeletion > 30 || daysUntilDeletion < 0)
      return { severity: undefined, action: '', message: '' };

    return {
      severity: 'warning',
      message:
        'This post will expire soon according to the data storage terms.',
      action:
        daysUntilDeletion === 0
          ? 'The content will expire later today.'
          : `${daysUntilDeletion} day(s) until expiration.`
    };
  }, [violationData, user]);

  const termsAlert = getTermsAlert();

  return (
    <Page
      title="Violations"
      spacing={0}
      header={
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Typography
              sx={{ cursor: 'pointer', color: 'text.secondary' }}
              onClick={handleGoBack}
              component="h1"
            >
              Violations
            </Typography>
            <ChevronRight />
            {isLoading ? (
              <Skeleton variant="text" width={50} />
            ) : (
              <Typography variant="body2">
                <strong>{currentPost}</strong> of {totalPosts}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Badge
              badgeContent={updatesCount}
              sx={{
                '& .MuiBadge-badge': {
                  right: 4,
                  backgroundColor: 'secondary.main',
                  color: 'common.white'
                }
              }}
            >
              <Button
                id="violation-detail-changes"
                onClick={() => setIsModalOpen(true)}
                size="small"
                text="Changes"
              />
            </Badge>
            <Button
              disabled={!previousPostId || isError}
              startIcon={<ArrowBack />}
              onClick={handleClickPreviousPost}
              size="small"
              text="Previous"
            />
            <Button
              id="violation-detail-next"
              disabled={!nextPostId || isError}
              endIcon={<ArrowForward />}
              onClick={handleClickNextPost}
              size="small"
              text="Next"
            />
          </Box>
        </Box>
      }
    >
      <>
        {termsAlert.message && (
          <Alert
            sx={{ alignItems: 'center', mb: 1 }}
            severity={termsAlert.severity}
            content={termsAlert.message}
            action={termsAlert.action}
          />
        )}
        <ViolationLayout />
        {violation && (
          <UpdatesDialog
            isOpen={isModalOpen}
            context={violation}
            updates={violationData}
            onClose={() => setIsModalOpen(false)}
            config={violationsConfig}
          />
        )}
      </>
    </Page>
  );
};

const ViolationDetails = () => {
  const { postId } = useParams();

  if (!postId) return <NotFound />;

  return (
    <ViolationProvider
      postId={Number(postId)}
      urlParams={queryString.parse(window.location.search)}
    >
      <ViolationContainer />
    </ViolationProvider>
  );
};

export default ViolationDetails;
